// ============================================================
// BOOKING TABLE ROW COMPONENT
// ============================================================

import { cn } from '../../lib/utils';
import { Booking } from '../../types/booking.types';
import { formatAmount, formatDateRange, getSpaceTypeInfo, getStatusConfig } from '../../utils/booking.utils';

interface BookingTableRowProps {
  booking: Booking;
  onRowClick: (booking: Booking) => void;
}

export function BookingTableRow({ booking, onRowClick }: BookingTableRowProps) {
  const statusConfig = getStatusConfig(booking.status);
  const typeInfo = getSpaceTypeInfo(booking.typeValue);
  const TypeIcon = typeInfo.icon;
  const StatusIcon = statusConfig.icon;

  return (
    <tr onClick={() => onRowClick(booking)} className="hover:bg-slate-50/80 transition-colors group cursor-pointer">
      <td className="px-5 py-3.5 pl-6 align-middle font-semibold text-primary whitespace-nowrap">{booking.ref}</td>

      <ClientCell client={booking.client} createdAt={booking.createdAt} />

      <SpaceCell
        typeIcon={TypeIcon}
        spaceName={booking.name}
        dateRange={formatDateRange(booking.startDate, booking.endDate)}
      />

      <td className="px-5 py-3.5 align-middle font-bold text-slate-900 whitespace-nowrap">
        {formatAmount(booking.price)}
      </td>

      <td className="px-5 py-3.5 align-middle text-slate-600">{booking.paymentMethod}</td>

      <StatusCell icon={StatusIcon} label={statusConfig.label} bg={statusConfig.bg} text={statusConfig.text} />

      <ActionsCell status={booking.status} canQuote={booking.canQuote} />
    </tr>
  );
}

function ClientCell({ client, createdAt }: { client: string; createdAt: string }) {
  const formattedDate = new Date(createdAt).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <td className="px-5 py-3.5 align-middle">
      <div className="flex flex-col justify-center">
        <span className="font-medium text-slate-900">{client}</span>
        <span className="text-xs text-slate-400 mt-0.5">{formattedDate}</span>
      </div>
    </td>
  );
}

interface SpaceCellProps {
  typeIcon: React.ComponentType<{ className?: string }>;
  spaceName: string;
  dateRange: string;
}

function SpaceCell({ typeIcon: TypeIcon, spaceName, dateRange }: SpaceCellProps) {
  return (
    <td className="px-5 py-3.5 align-middle">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 bg-white border border-slate-200 shadow-sm rounded-md">
          <TypeIcon className="w-3.5 h-3.5 text-slate-500" />
        </div>
        <span className="text-slate-700 font-bold">{spaceName}</span>
      </div>
      <div className="text-xs text-slate-500 pl-9">
        <span className="text-slate-400">{dateRange}</span>
      </div>
    </td>
  );
}

interface StatusCellProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  bg: string;
  text: string;
}

function StatusCell({ icon: StatusIcon, label, bg, text }: StatusCellProps) {
  return (
    <td className="px-5 py-3.5 align-middle">
      <span
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase border border-white/20 whitespace-nowrap',
          bg,
          text,
        )}
      >
        <StatusIcon className="w-3.5 h-3.5 flex-shrink-0" />
        {label}
      </span>
    </td>
  );
}

function ActionsCell({ status, canQuote }: { status: string; canQuote: boolean }) {
  return (
    <td className="px-5 py-3.5 pr-6 text-right align-middle">
      <div className="flex items-center justify-end gap-2">
        {status === 'PENDING' && (
          <>
            <ActionButton label="Accepter" variant="success" />
            <ActionButton label="Refuser" variant="danger" />
          </>
        )}
        {canQuote && <ActionButton label="Devis" variant="neutral" />}
      </div>
    </td>
  );
}

interface ActionButtonProps {
  label: string;
  variant: 'success' | 'danger' | 'neutral';
}

function ActionButton({ label, variant }: ActionButtonProps) {
  const variantStyles = {
    success: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
    danger: 'bg-rose-100 text-rose-700 hover:bg-rose-200',
    neutral: 'border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300',
  };

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center rounded px-2.5 py-1 text-xs font-semibold transition-colors',
        variantStyles[variant],
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </button>
  );
}
